import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Menu, ArrowRight, ChevronRight, X } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { LogoFull } from './Logo';
interface NavbarProps {
  onNavigate?: (page: 'home' | 'fitur' | 'kontak') => void;
}
export function Navbar({
  onNavigate
}: NavbarProps) {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  const navLinks = [{
    name: t.nav.platform,
    href: '#features'
  }, {
    name: t.nav.solutions,
    href: '#solutions'
  }, {
    name: t.nav.integrations,
    href: '#technology'
  }, {
    name: t.nav.resources,
    href: '#impact'
  }];
  const closeMobile = () => setMobileOpen(false);
  return <>
      <nav className={`fixed left-0 right-0 top-0 z-50 bg-white/95 backdrop-blur-md transition-all duration-300 ${isScrolled ? 'py-3 shadow-md border-b border-forest-100' : 'py-4 border-b border-transparent'}`} data-id="element-248">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between gap-4" data-id="element-249">
          {/* Logo */}
          <button onClick={() => onNavigate?.('home')} className="flex items-center flex-shrink-0" data-id="element-250">
            <LogoFull size={34} data-id="element-251" />
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center" data-id="element-252">
            {navLinks.map(link => <a key={link.name} href={link.href} className="text-sm font-medium text-forest-700 hover:text-brand-green hover:bg-forest-50 px-3.5 py-2 rounded-lg transition-all duration-200 whitespace-nowrap" data-id="element-253">
                {link.name}
              </a>)}
            <button onClick={() => onNavigate?.('kontak')} className="text-sm font-medium text-forest-700 hover:text-brand-green hover:bg-forest-50 px-3.5 py-2 rounded-lg transition-all duration-200 whitespace-nowrap" data-id="element-254">
              {t.nav.pricing}
            </button>
          </div>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0" data-id="element-255">
            <div className="flex items-center border border-forest-200 rounded-lg overflow-hidden bg-forest-50 text-xs font-semibold" data-id="element-256">
              <button onClick={() => setLang('id')} className={`px-2.5 py-1.5 transition-colors ${lang === 'id' ? 'bg-brand-green text-white' : 'text-forest-600 hover:text-forest-900 hover:bg-forest-100'}`} data-id="element-257">
                ID
              </button>
              <button onClick={() => setLang('en')} className={`px-2.5 py-1.5 transition-colors ${lang === 'en' ? 'bg-brand-green text-white' : 'text-forest-600 hover:text-forest-900 hover:bg-forest-100'}`} data-id="element-258">
                EN
              </button>
            </div>
            <div className="w-px h-5 bg-forest-100" data-id="element-259" />
            <button onClick={() => onNavigate?.('fitur')} className="text-sm font-medium text-forest-700 hover:text-brand-green hover:bg-forest-50 px-3.5 py-2 rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1.5" data-id="element-260">
              {t.nav.fitur}
              <ArrowRight className="w-3.5 h-3.5" data-id="element-261" />
            </button>
            <Button onClick={() => {
            const el = document.getElementById('contact');
            if (el) el.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="bg-brand-green hover:bg-forest-600 text-white font-semibold text-sm h-9 px-5 rounded-lg transition-all shadow-sm hover:shadow-md whitespace-nowrap" data-id="element-262">
              {t.nav.getStarted}
            </Button>
          </div>

          {/* Mobile Right: Lang + Hamburger */}
          <div className="lg:hidden flex items-center gap-2" data-id="element-263">
            <div className="flex items-center border border-forest-200 rounded-lg overflow-hidden bg-forest-50 text-xs font-semibold" data-id="element-264">
              <button onClick={() => setLang('id')} className={`px-2.5 py-1.5 transition-colors ${lang === 'id' ? 'bg-brand-green text-white' : 'text-forest-600'}`} data-id="element-265">
                ID
              </button>
              <button onClick={() => setLang('en')} className={`px-2.5 py-1.5 transition-colors ${lang === 'en' ? 'bg-brand-green text-white' : 'text-forest-600'}`} data-id="element-266">
                EN
              </button>
            </div>
            <button onClick={() => setMobileOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-lg text-forest-700 hover:bg-forest-50 transition-colors" aria-label="Open menu" data-id="element-267">
              <Menu className="w-5 h-5" data-id="element-268" />
            </button>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div style={{
      height: isScrolled ? 57 : 65
    }} data-id="element-269" />

      {/* ── MOBILE MENU OVERLAY ── */}
      {/* Backdrop */}
      <div onClick={closeMobile} className={`lg:hidden fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} aria-hidden="true" data-id="element-270" />

      {/* Slide-in Panel */}
      <div className={`lg:hidden fixed top-0 right-0 bottom-0 z-[70] w-[300px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-271">
        {/* Panel Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-forest-100 flex-shrink-0" data-id="element-272">
          <LogoFull size={30} data-id="element-273" />
          <button onClick={closeMobile} className="w-8 h-8 flex items-center justify-center rounded-lg text-forest-500 hover:text-forest-900 hover:bg-forest-100 transition-colors" aria-label="Close menu" data-id="element-274">
            <X className="w-4 h-4" data-id="element-275" />
          </button>
        </div>

        {/* Nav Links — scrollable middle */}
        <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-276">
          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-widest px-3 mb-3" data-id="element-277">
            Menu
          </p>
          <div className="flex flex-col gap-1" data-id="element-278">
            {navLinks.map(link => <a key={link.name} href={link.href} onClick={closeMobile} className="flex items-center justify-between text-forest-700 hover:text-brand-green hover:bg-forest-50 text-sm font-medium py-3 px-3 rounded-lg transition-colors group" data-id="element-279">
                <span data-id="element-280">{link.name}</span>
                <ChevronRight className="w-4 h-4 text-forest-300 group-hover:text-brand-green transition-colors flex-shrink-0" data-id="element-281" />
              </a>)}
            <button onClick={() => {
            onNavigate?.('kontak');
            closeMobile();
          }} className="flex items-center justify-between w-full text-forest-700 hover:text-brand-green hover:bg-forest-50 text-sm font-medium py-3 px-3 rounded-lg transition-colors group" data-id="element-282">
              <span data-id="element-283">{t.nav.pricing}</span>
              <ChevronRight className="w-4 h-4 text-forest-300 group-hover:text-brand-green transition-colors flex-shrink-0" data-id="element-284" />
            </button>

            <div className="h-px bg-forest-100 my-2" data-id="element-285" />

            <button onClick={() => {
            onNavigate?.('fitur');
            closeMobile();
          }} className="flex items-center justify-between w-full text-brand-green bg-forest-50 border border-forest-200 text-sm font-semibold py-3 px-3 rounded-lg hover:bg-forest-100 transition-colors" data-id="element-286">
              <span data-id="element-287">{t.nav.fitur}</span>
              <ArrowRight className="w-4 h-4 text-brand-green flex-shrink-0" data-id="element-288" />
            </button>
          </div>
        </div>

        {/* CTA Footer */}
        <div className="flex-shrink-0 px-4 py-5 border-t border-forest-100 bg-forest-50/50 space-y-3" data-id="element-289">
          <Button onClick={() => {
          closeMobile();
          setTimeout(() => {
            const el = document.getElementById('contact');
            if (el) el.scrollIntoView({
              behavior: 'smooth'
            });
          }, 300);
        }} className="w-full bg-brand-green hover:bg-brand-green/90 text-white font-semibold h-11 rounded-lg text-sm shadow-sm" data-id="element-290">
            {t.nav.getStarted}
          </Button>
          <p className="text-center text-xs text-forest-400" data-id="element-291">{t.nav.noCard}</p>
        </div>
      </div>
    </>;
}